import { Button, Col, Container, ProgressBar, Row, Stack } from "react-bootstrap";
import { useAppDispatch } from "../../redux/store";
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook";
import { useCallback, useEffect, useState } from "react";
import IcalEntry from "../../Components/Icals/IcalEntry/IcalEntry";
import timeService from "../../services/time/timeService";
import { UseAllIcalsDecorated } from "../../Hooks/UseIcals/UseIcalsDecorated";
import { convertIcalToBookings } from "../../Helpers/Icals/IcalHelpers";
import { navigateTo } from "../../redux/router/routerActions";
import IcalFilterForm from "../../Components/Forms/IcalFilterForm/IcalFilterForm";
import UseIcalsFilter from "../../Hooks/UseIcals/UseIcalsFilter";
import { IcalEntryType } from "../../Types/icalEntryType";
import { bClientApi, useGetBookingsQuery, useLazyGetPropertyIcalDataQuery, useImportBookingsMutation } from "../../services/bClientApi";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { accountSelector } from "../../redux/account/accountSlice";

export default function IcalsPage() {

    const baseUserInfo = useSelector(accountSelector)
    const {
        _id: userId = 'server_icalConvert',
    } = baseUserInfo || {}

    const [icalDataSet, setIcalData] = useState<any>({
        icalData: {},
        issues: [],
        isLoading: false,
        progress: {
            count: 0,
            total: 0
        }
    })

    const progress = icalDataSet.progress
    const icalData = icalDataSet.icalData

    const [getPropertyIcalData] = useLazyGetPropertyIcalDataQuery({
        refetchOnFocus: false,
    })

    const [importBookings] = useImportBookingsMutation()
    const dispatch: any = useAppDispatch()

    const { myProperties, publicPropertyIdKeys } = useMyProperties()

    const gatherAllIcalData = useCallback(async (forceFetch: boolean = false) => {
        if (publicPropertyIdKeys.length === 0) return
        if (icalDataSet.isLoading) return

        // clear ical data
        setIcalData({
            icalData: {},
            issues: [],
            isLoading: true,
            progress: {
                count: 0,
                total: 0
            }
        })
        const propertyIds = publicPropertyIdKeys
        const tempIcalData: any = {}
        const icalIssues: any = []
        try {
            for (const propertyId of propertyIds) {
                console.log(propertyId)
                const propertyIcalData = await getPropertyIcalData(`${propertyId}${forceFetch ? '?forceIcal=true' : ''}`).unwrap().catch((error: any) => {
                    console.error('Error fetching iCal data for property:', propertyId, error);
                    return { error: true }
                });
                if (propertyIcalData.error) {
                    tempIcalData[propertyId] = []
                    const propertyShortName = myProperties[propertyId] ? myProperties[propertyId].title.split(' - ')[0] : propertyId
                    icalIssues.push({
                        propertyId,
                        message: `Error fetching iCal data for property: ${propertyId} - ${propertyShortName}`,
                        type: 'error'
                    })
                    continue
                }
                tempIcalData[propertyId] = propertyIcalData?.propertyIcalData || []
                if (propertyIcalData?.badCalendars && propertyIcalData?.badCalendars.length > 0) {
                    const propertyShortName = myProperties[propertyId] ? myProperties[propertyId].title.split(' - ')[0] : propertyId
                    icalIssues.push({
                        propertyId,
                        message: `Bad Calendars for property: ${propertyId} - ${propertyShortName} - ${propertyIcalData.badCalendars.join(', ')}`,
                        type: 'warning'
                    })
                }
                setIcalData({
                    icalData: tempIcalData,
                    issues: icalIssues,
                    isLoading: true,
                    progress: {
                        count: Object.keys(tempIcalData).length,
                        total: Object.keys(propertyIds).length
                    }
                })
            }
            setIcalData({
                icalData: tempIcalData,
                issues: icalIssues,
                isLoading: false,
                progress: {
                    count: Object.keys(tempIcalData).length,
                    total: Object.keys(propertyIds).length
                }
            })
        } catch (error) {
            console.log('error', error)
        }
    }, [getPropertyIcalData, publicPropertyIdKeys, icalDataSet, myProperties])

    useEffect(() => {
        if (publicPropertyIdKeys.length > 0 && !icalDataSet.isLoading && icalDataSet.progress.total === 0) {
            gatherAllIcalData()
        }
        return () => { }
    }, [publicPropertyIdKeys, icalDataSet, gatherAllIcalData])


    const startOfYesterday = timeService.getCurrentTime().minus({ days: 1 }).startOf('day').toMillis()
    const activeQuery = {
        query: {
            checkIn: startOfYesterday,
            bookingStatus: ['confirmed', 'pending', 'new'],
        },
        limit: 500,
        page: 1
    }

    const {
        data,
    } = useGetBookingsQuery({
        bookingsQuery: {
            propertyId: publicPropertyIdKeys,
            ...activeQuery.query
        },
        pagination: {
            page: 1,
            limit: 500,
            all: true,
        }
    }, { skip: Object.keys(myProperties).length === 0 })

    const bookingsList = data?.docs || []

    const [icalFilter, setIcalFilter]: any = useState({
        checkIn: new Date(startOfYesterday),
        checkOut: null,
        source: '',
        hideExisting: true,
        hideBlocked: true,
    })

    const allIcalData = UseAllIcalsDecorated({
        icalData,
        bookingsList,
        myProperties,
    })

    const filteredIcalData = UseIcalsFilter(allIcalData, icalFilter)

    const convertIcalBooking = async (iCalEntry: any) => {
        const bookingsToAdd = convertIcalToBookings(
            {
                ...iCalEntry,
                createdBy: userId,
            },
            myProperties
        )
        const newBookings = await importBookings(bookingsToAdd).unwrap()
        if (newBookings && newBookings.acknowledged) {
            dispatch(navigateTo(`/bookings?query={"firstName":"${bookingsToAdd[0].contactInfo?.firstName}","lastName":"${bookingsToAdd[0].contactInfo?.lastName}"}`))
        }
    }

    const updateIcalFilter = (values: any) => {
        setIcalFilter({
            ...values,
        })
    }

    return (
        <Container>
            <Row>
                <Col>
                    <h1>Icals Page</h1>
                    <hr />

                    <h3>All Icals</h3>

                    {progress.total !== progress.count ? (
                        <>
                            Loading <br />
                            <ProgressBar now={progress.total > 0 ? (progress.count / progress.total) * 100 : 0} label={`${progress.count} of ${progress.total}`} />
                        </>
                    ) : (
                        <Button
                            size={'sm'}
                            variant={'warning'}
                            onClick={() => {
                                dispatch(bClientApi.util.invalidateTags(['IcalData']))
                                gatherAllIcalData(true)
                            }}>
                            Hard Refresh
                        </Button>
                    )}

                    {icalDataSet.issues && icalDataSet.issues.length > 0 && (
                        <div>
                            <h4>Issues</h4>
                            <ul>
                                {icalDataSet.issues.map((issue: any, idx: number) => (
                                    <li key={idx}>
                                        {issue.message} &nbsp;
                                        <Link to={`/property/${issue.propertyId}`} target="_blank" rel="noopener noreferrer">
                                            View
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {bookingsList && bookingsList.length > 0 && (
                        <div>{bookingsList.length} bookings</div>
                    )}

                    <h3>Ical Bookings</h3>
                    <IcalFilterForm
                        checkIn={icalFilter.checkIn}
                        checkOut={icalFilter.checkOut ? icalFilter.checkOut : null}
                        hideExisting={icalFilter.hideExisting}
                        hideBlocked={icalFilter.hideBlocked}
                        source={icalFilter.source}
                        onSubmit={updateIcalFilter}
                    />

                    {allIcalData && allIcalData.length > 0 && filteredIcalData && (
                        <>
                            <Stack gap={3}>
                                {filteredIcalData.map((icalInfo: IcalEntryType, idx: number) => (
                                    <IcalEntry
                                        key={`${idx}-${icalInfo.unit}-${icalInfo.source}`}
                                        icalData={icalInfo}
                                        convertIcalBooking={convertIcalBooking}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}

                </Col>
            </Row>
        </Container>
    )
}